import { ColorScaleConfig, getColorForValue } from '@/utils/colorScale'
import { RGBA } from '@/utils/rgba'
import { computed, ref, watch } from 'vue'
import { useMapboxFeatureState } from '@/pinia/mapboxFeatureState'
import { defineStore } from 'pinia'

export type ColoredEntityData = {
  id: string
  meta?: Record<string, any>
  value: number
}

const DEFAULT_COLOR_SCALE: ColorScaleConfig = {
  type: 'SINGLE',
  cutoffs: [{ color: RGBA.transparent().toHex() }]
}

type StateInstance = {
  key: string
  data: ColoredEntityData[] | undefined
  colorScale?: ColorScaleConfig
  title: string
  unit: string
  formatter: (value: number) => string
}

export default defineStore('colored-entities', () => {
  const currentKey = ref<string>()
  const data = ref<ColoredEntityData[]>()
  const colorScale = ref<ColorScaleConfig>(DEFAULT_COLOR_SCALE)
  const title = ref<string>('')
  const unit = ref<string>('')
  const formatter = ref((value: number) => value.toString())
  const active = computed<boolean>(() => !!data.value?.length)
  const { setRecords, resetRecords } = useMapboxFeatureState()

  const mean = computed(() => {
    if (!data.value || data.value.length === 0) return 0
    const sum = data.value.reduce((acc, curr) => acc + curr.value, 0)
    return sum / data.value.length
  })

  function setColoredBuildings(s: StateInstance) {
    resetRecords()
    currentKey.value = s.key
    data.value = s.data
    if (s.colorScale) colorScale.value = s.colorScale
    title.value = s.title
    unit.value = s.unit
    formatter.value = s.formatter
  }

  function setColorScale(cs: ColorScaleConfig) {
    colorScale.value = cs
  }

  function resetColoredBuildings(resetKey: boolean = false) {
    resetRecords()
    if (resetKey) {
      currentKey.value = undefined
    }
    data.value = undefined
    colorScale.value = {
      type: 'SINGLE',
      cutoffs: [{ color: RGBA.transparent().toHex() }]
    }
    title.value = ''
    unit.value = ''
    formatter.value = (value: number) => value.toString()
  }

  watch(
    [data, colorScale],
    ([data, colorScale]) => {
      if (!data || !colorScale) return
      setRecords(
        Object.fromEntries(
          data.map((d) => [d.id, { color: getColorForValue(colorScale, d.value).toString() }])
        )
      )
    },
    { immediate: true }
  )

  return {
    set: setColoredBuildings,
    reset: resetColoredBuildings,
    setColorScale,
    active,
    currentKey,
    data,
    mean,
    colorScale,
    title,
    unit,
    formatter
  }
})
